"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Zap, Settings, TrendingUp, ChartBar, Rocket, FileText } from "lucide-react";
export default function PathToPeakPerformance() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const steps = [{
    id: 1,
    icon: Zap,
    title: "NEURO ASSESSMENT",
    description: "We begin with a comprehensive evaluation of your nervous system and movement patterns to identify the root cause of pain."
  }, {
    id: 2,
    icon: Settings,
    title: "SENSORY MAPPING",
    description: "Our specialists map your sensory inputs to determine where neural signals are misfiring or causing performance blocks."
  }, {
    id: 3,
    icon: TrendingUp,
    title: "TARGETED TRAINING",
    description: "We implement specific Square 1 neuro-sensory drills designed to recalibrate your system and restore optimal athletic function."
  }, {
    id: 4,
    icon: ChartBar,
    title: "MOVEMENT REFINEMENT",
    description: "One-on-one treatment sessions focus on refining biomechanics to ensure your body moves with maximum efficiency."
  }, {
    id: 5,
    icon: Rocket,
    title: "PEAK INTEGRATION",
    description: "We integrate your new neural patterns into high-intensity athletic movements to solidify your performance gains."
  }, {
    id: 6,
    icon: FileText,
    title: "ONGOING OPTIMIZATION",
    description: "Continuous monitoring and data-driven adjustments keep you at the top of your game and prevent future injury."
  }];
  return <section data-section-id="261857" id="path-to-peak-performance" className="bg-background text-foreground py-16 lg:py-24">
      <div className="page-container">
        <div ref={ref} className="mb-12 text-center">
          <h2 className="mb-4 font-default text-3xl font-bold uppercase tracking-wider text-foreground lg:text-4xl">
            PATH TO PEAK PERFORMANCE
          </h2>
          <p className="mx-auto max-w-2xl text-lg font-default text-muted-foreground uppercase">
            A data-driven six-step neuro-sensory process to eliminate pain and unlock your elite athletic potential.
          </p>
        </div>

        <div className="relative mx-auto max-w-4xl">
          {}
          <div className="absolute left-1/2 top-0 hidden h-full w-px -translate-x-1/2 bg-gradient-to-b from-primary via-primary to-secondary lg:block opacity-20" />

          <div className="space-y-8 lg:space-y-12">
            {steps.map((step, index) => {
            const Icon = step.icon;
            const isEven = index % 2 === 0;
            const {
              ref: stepRef,
              isVisible: stepVisible
            } = useRevealOnScroll();
            return <div key={step.id} data-index={index} ref={stepRef} className={`transition-all duration-700 ${stepVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
                  <div className="flex flex-col items-center gap-6 lg:flex-row lg:gap-12">
                    {}
                    <div className={`w-full lg:w-5/12 ${isEven ? "lg:order-1" : "lg:order-3"}`}>
                      <Card className="border border-border bg-card rounded-md shadow-sm hover:border-primary transition-colors duration-300">
                        <CardHeader>
                          <CardTitle className="font-default text-xl font-bold uppercase tracking-wide text-foreground">
                            {step.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <CardDescription className="text-base font-default text-muted-foreground leading-relaxed">
                            {step.description}
                          </CardDescription>
                        </CardContent>
                      </Card>
                    </div>

                    {}
                    <div className={`z-10 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-md lg:order-2 ${stepVisible ? "scale-100" : "scale-75"} transition-transform duration-500`}>
                      <Icon className="h-8 w-8" />
                    </div>

                    {}
                    <div className={`hidden w-5/12 lg:flex ${isEven ? "lg:order-3" : "lg:order-1"}`} />
                  </div>

                  {}
                  <div className="flex items-center justify-center gap-3 lg:hidden">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-sm font-bold text-primary-foreground">
                      {step.id}
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="mt-16 text-center lg:mt-20">
          <p className="mb-6 text-lg font-default font-bold italic uppercase text-foreground">
            READY TO REWIRE YOUR PERFORMANCE?
          </p>
          <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-md font-default font-bold uppercase tracking-widest px-8" asChild>
            <a href="#start-your-recovery">Ready to Get Started?</a>
          </Button>
        </div>
      </div>
    </section>;
}